// JWT timing settings
// All timings are in seconds

export type OrgJwtTimings = {
  ACCESS_TOKEN_EXPIRY: number // How long the JWT access token is valid
  REFRESH_TOKEN_EXPIRY: number // Maximum period of inactivity before re-login required
  SESSION_REFRESH_INTERVAL: number // How long before expiry to trigger proactive refresh
}

// ACCESS_TOKEN_EXPIRY must stay <= the minimum idle timeout the update endpoint allows (900s).
// The access token is a stateless JWT the backend accepts until its own exp, so an access expiry
// above the idle floor would let a cached token outlive a shorter per-org idle window.
// SESSION_REFRESH_INTERVAL must stay < ACCESS_TOKEN_EXPIRY (it is the proactive-refresh lead time).
export const DEFAULT_TIMINGS: OrgJwtTimings = {
  ACCESS_TOKEN_EXPIRY: 15 * 60, // 15 minutes
  REFRESH_TOKEN_EXPIRY: 7 * 24 * 60 * 60, // 7 days
  SESSION_REFRESH_INTERVAL: 5 * 60, // 5 minutes
}

// The pre-auth organisation-picker token grants no org access and cannot be refreshed (it is not
// fullyAuthenticated), so it is decoupled from the short access-token expiry: tying it to the 15m
// access window would log multi-org users out of the picker after ~10m. Re-selection re-issues it.
export const ORGANISATION_PICKER_TOKEN_EXPIRY = 60 * 60 // 1 hour

const currentTimeInSeconds = () => Math.floor(Date.now() / 1000)

export const getIssuedAt = () => currentTimeInSeconds()

export const getAccessTokenExpirationTime = (customExpiry?: number) =>
  currentTimeInSeconds() + (customExpiry ?? DEFAULT_TIMINGS.ACCESS_TOKEN_EXPIRY)

export const getRefreshTokenExpirationTime = (customExpiry: number) => currentTimeInSeconds() + customExpiry

// A per-org idle timeout (organisations.idle_session_timeout_seconds) overrides the static
// fallback for REFRESH_TOKEN_EXPIRY only. NULL means "not configured" -> use the fallback.
export const resolveRefreshTokenExpirySeconds = (orgIdleTimeoutSeconds: number | null, fallbackSeconds: number): number =>
  orgIdleTimeoutSeconds ?? fallbackSeconds
